import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
// Case conversation via https://github.com/mpyw/axios-case-converter#usage
import applyCaseMiddleware from 'axios-case-converter';
import i18next from 'i18next';
import { Capacitor } from '@capacitor/core';
import {
  getAuthorizedUrl,
  shouldUseAuthorizedEndpoint,
} from 'shared/authorizedUtil';
import { getAccessKey, clearDataForLogout } from './state/localStorage';
import { DEFAULT_LOCALE } from './i18n/utils';
import { getAppVersion } from './shared/envUtils';
import { ENV } from './shared/ENV';
import { shouldUseNoCacheHeader } from './shared/noCacheUtil';

// See https://github.com/axios/axios#request-config for options
const baseApiHeaders: AxiosRequestConfig['headers'] = {};

const baseApi = axios.create({
  baseURL: ENV.VITE_BASE_API_URL,
  headers: baseApiHeaders,
});

const NO_LOGOUT_URL_ALLOWLIST = [
  'recurly_integration/payment_verification/', // for verifying a membership client token
  'organization/sso/callback/', // for handling SSO callback
];

export const getAuthenticationHeaders = async (): Promise<
  Record<string, string>
> => {
  const token = await getAccessKey();
  if (!token) {
    return {};
  }
  return {
    Authorization: `Token ${token}`,
  };
};

const applyInterceptors = (apiInstance: AxiosInstance): AxiosInstance => {
  apiInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const appVersion = await getAppVersion();

      const requestUrl = config.url ?? '';
      if (shouldUseAuthorizedEndpoint(requestUrl)) {
        // eslint-disable-next-line no-param-reassign
        config.url = getAuthorizedUrl(requestUrl);
      }

      const includeNoCache = shouldUseNoCacheHeader();
      const lang = i18next.resolvedLanguage || DEFAULT_LOCALE;

      config.headers.set('Accept-Language', lang);
      config.headers.set('X-Git-Tag', ENV.VITE_GIT_TAG || 'local');
      config.headers.set('X-App-Version', appVersion);
      config.headers.set(
        'X-App-Is-Native',
        String(Capacitor.isNativePlatform()),
      );

      // Add authentication headers
      const authHeaders = await getAuthenticationHeaders();
      Object.entries(authHeaders).forEach(([key, value]) => {
        config.headers.set(key, value);
      });

      // Add no-cache header if needed
      if (includeNoCache) {
        config.headers.set('X-No-Cache', '1');
      }

      return config;
    },
  );

  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // If the user is no longer authorized or the token was deleted
      // on the server, we must log out the user.
      if (
        error?.response?.status === HttpStatusCode.Unauthorized &&
        !NO_LOGOUT_URL_ALLOWLIST.includes(error?.config.url)
      ) {
        window.location.assign('/login');
        await clearDataForLogout();
      }
      return Promise.reject(error);
    },
  );

  return apiInstance;
};

export const API = applyInterceptors(applyCaseMiddleware(baseApi));

export const MapBoxAPI = applyCaseMiddleware(
  axios.create({
    baseURL: 'https://api.mapbox.com/geocoding/v5/mapbox.places/',
  }),
);
